import clsx from "clsx";
import { Check, ChevronRight } from "lucide-react";
import { type ReactNode, type Ref, useId } from "react";

import { Badge, type BlockableProps, type IconProp, renderIcon, SectionLabel } from "./ui";
import { t } from "../i18n/i18n";

/** Briques du contenu des panneaux et des fiches (R4, R7), reprises du panneau Manœuvre : groupe titré, ligne de
 *  liste, ligne d'arbre, propriété, section de carte, tuile chiffrée, carte de choix, barre d'action, note, et
 *  dépliage animé. Hauteurs Prevail : ligne de liste ou de fiche 32 px au moins (`min-h-8 py-1.5`), ligne d'arbre
 *  24 px avec 14 px de retrait par niveau, gouttière de 16 px. Rien ne touche un filet gris : le séparateur est la
 *  bordure du groupe ou de la propriété, jamais celle d'un élément cliquable. Durées : `--gx-enter` seulement. */

// --- Dépliage ------------------------------------------------------------------------------------------------------

/** Contenu qui se déplie en hauteur, en fondu (`.gx-collapse`, durée `--gx-enter`, fondu seul en mouvement réduit).
 *  Fermé, il est `inert` (ni focus ni lecture). `id` : cible de l'`aria-controls` du bouton qui le pilote. */
export function Disclosure({
  open,
  id,
  children,
  className,
}: {
  open: boolean;
  id?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div id={id} className={clsx("gx-collapse", className)} data-open={open} inert={!open}>
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}

/** Chevron d'un dépliage : `ChevronRight` de 14 px qui pivote de 90° à l'ouverture. */
export function DisclosureChevron({ open, className }: { open: boolean; className?: string }) {
  return (
    <ChevronRight
      size={14}
      aria-hidden
      className={clsx(
        "shrink-0 text-ink-muted transition-transform duration-(--gx-enter) ease-(--gx-ease-out) motion-reduce:transition-none",
        open && "rotate-90",
        className,
      )}
    />
  );
}

// --- Groupes et lignes ---------------------------------------------------------------------------------------------

/** Groupe d'un panneau (R7) : titre en casse normale, 12 px, `ink-muted` (les petites capitales sont réservées à la
 *  barre latérale et aux rubriques de vue), compteur en `Badge`, actions à droite (`IconButton size="xs"` ou
 *  `"sm"`), filet en bas avec 8 px de part et d'autre (sauf le dernier). `leading` : pastille d'étape ou icône
 *  avant le titre. Les lignes (`PanelRow`) sont posées dans une gouttière de 8 px ; `flush` la retire (contenu qui
 *  gère ses propres marges, `px-4`). Sans `title`, le groupe n'a pas d'en-tête. */
export function PanelGroup({
  title,
  leading,
  count,
  actions,
  flush = false,
  children,
  className,
}: {
  title?: ReactNode;
  leading?: ReactNode;
  count?: number;
  actions?: ReactNode;
  flush?: boolean;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <section className={clsx("border-b border-line py-2 last:border-b-0", className)}>
      {(title !== undefined || actions !== undefined) && (
        // Compteur seul à droite : même bord droit que les compteurs des lignes (px-2 du contenu + pr-1 de la
        // ligne = 12 px), pour que tous les chiffres d'un panneau soient alignés (demande d'Alexis du 15/10/2026).
        <div className={clsx("flex min-h-8 items-center gap-2 pl-4", count !== undefined && actions === undefined ? "pr-3" : "pr-4")}>
          {leading}
          <h3 className="min-w-0 flex-1 text-ui leading-snug font-medium break-words text-ink-muted">{title}</h3>
          {count !== undefined && <Badge>{count}</Badge>}
          {actions !== undefined && <span className="flex shrink-0 items-center gap-0.5">{actions}</span>}
        </div>
      )}
      {children !== undefined && <div className={flush ? undefined : "space-y-px px-2"}>{children}</div>}
    </section>
  );
}

/** Ligne de liste (R4) : icône de 14 px dans une case de 20 px, titre quasi noir de poids moyen, seconde ligne
 *  grise, état (`Badge dot`) et actions à droite. Aucun texte coupé au milieu d'un mot : titre et détail passent à
 *  la ligne (`break-words`). Sélection en `bg-selected` (une seule ligne à la fois) ; avec `onClick`, la ligne se
 *  surligne au survol. Les attributs `data-*` sont reportés sur la ligne (cibles de `designate()`).
 *  `option` : ligne d'une liste de résultats pilotée au clavier depuis un champ (motif combobox) — rôle `option`,
 *  identifiant pour `aria-activedescendant`, ligne active surlignée comme au survol (`bg-hover`, pas `bg-selected`) ;
 *  le focus reste dans le champ. */
export function PanelRow({
  icon,
  title,
  detail,
  state,
  actions,
  selected,
  onClick,
  clickTitle,
  className,
  option,
  ...rest
}: {
  icon?: IconProp;
  title: ReactNode;
  detail?: ReactNode;
  state?: ReactNode;
  actions?: ReactNode;
  selected?: boolean;
  onClick?: () => void;
  /** Info-bulle du bouton de la ligne. */
  clickTitle?: string;
  className?: string;
  option?: { id: string; active: boolean };
  [data: `data-${string}`]: string | undefined;
}) {
  const body = (
    <>
      {icon !== undefined && (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-ink-muted">{renderIcon(icon, 14)}</span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block text-ui leading-snug font-medium break-words text-ink">{title}</span>
        {detail && <span className="block text-meta leading-snug break-words text-ink-muted">{detail}</span>}
      </span>
    </>
  );
  return (
    <div
      {...rest}
      id={option?.id}
      role={option ? "option" : undefined}
      aria-selected={option ? option.active : undefined}
      className={clsx(
        "flex min-h-8 items-center gap-2 rounded-lg px-2 py-1.5 transition-colors",
        selected ? "bg-selected" : option?.active ? "bg-hover" : onClick && "hover:bg-hover",
        className,
      )}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          title={clickTitle}
          tabIndex={option ? -1 : undefined}
          aria-current={selected || undefined}
          className="focus-inset flex min-w-0 flex-1 items-center gap-2.5 text-left"
        >
          {body}
        </button>
      ) : (
        <span className="flex min-w-0 flex-1 items-center gap-2.5">{body}</span>
      )}
      {state}
      {actions && <span className="flex shrink-0 items-center gap-0.5">{actions}</span>}
    </div>
  );
}

/** Ligne d'arbre (R4) : 24 px de haut, retrait de 14 px par niveau (`depth`, 0 à la racine), chevron de dépliage
 *  si `onToggle`, icône de 14 px, libellé tronqué par des points de suspension, compteur en `Badge`, `trailing` à
 *  droite (bouton d'œil des Calques, par exemple). `toggleLabel` : nom du bouton de dépliage lu par les lecteurs
 *  d'écran quand `label` n'est pas un texte (par défaut le libellé texte, sinon « Déplier » / « Replier »). */
export function TreeRow({
  depth = 0,
  icon,
  label,
  count,
  expanded,
  onToggle,
  selected,
  onClick,
  trailing,
  title,
  toggleLabel,
  className,
}: {
  depth?: number;
  icon?: IconProp;
  label: ReactNode;
  count?: number;
  expanded?: boolean;
  onToggle?: () => void;
  selected?: boolean;
  onClick?: () => void;
  trailing?: ReactNode;
  title?: string;
  toggleLabel?: string;
  className?: string;
}) {
  const content = (
    <>
      {icon !== undefined && <span className="flex w-4 shrink-0 justify-center text-ink-muted">{renderIcon(icon, 14)}</span>}
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </>
  );
  return (
    <div
      className={clsx(
        "flex h-6 items-center gap-1.5 rounded-md pr-1 text-ui text-ink transition-colors",
        selected ? "bg-selected" : (onClick || onToggle) && "hover:bg-hover",
        className,
      )}
      style={{ paddingLeft: 4 + depth * 14 }}
      title={title}
    >
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={Boolean(expanded)}
          aria-label={toggleLabel ?? (typeof label === "string" ? label : expanded ? t("Replier") : t("Déplier"))}
          className="flex size-4 shrink-0 items-center justify-center rounded-sm"
        >
          <DisclosureChevron open={Boolean(expanded)} />
        </button>
      ) : (
        <span className="w-4 shrink-0" aria-hidden />
      )}
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          aria-current={selected || undefined}
          className="focus-inset flex h-full min-w-0 flex-1 items-center gap-1.5 text-left"
        >
          {content}
        </button>
      ) : (
        <span className="flex min-w-0 flex-1 items-center gap-1.5">{content}</span>
      )}
      {count !== undefined && <Badge>{count}</Badge>}
      {trailing}
    </div>
  );
}

/** Propriété d'une fiche (R4) : libellé gris à gauche, valeur quasi noire alignée à droite (`mono` : chasse fixe
 *  pour un GDH, une coordonnée ou un code), filet en bas sauf la dernière. La valeur passe à la ligne au besoin,
 *  jamais au milieu d'un mot. */
export function PropRow({
  label,
  mono = false,
  children,
  className,
}: {
  label: ReactNode;
  mono?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx("flex min-h-8 items-baseline justify-between gap-3 border-b border-line py-1.5 last:border-b-0", className)}>
      <span className="shrink-0 text-ui text-ink-muted">{label}</span>
      <span className={clsx("min-w-0 text-right text-ui break-words text-ink", mono && "font-mono tabular-nums")}>{children}</span>
    </div>
  );
}

/** Section d'une fiche d'objet : filet en haut, gouttière de 16 px, titre facultatif en petites capitales
 *  (`SectionLabel inset`) avec ses actions à droite. */
export function CardSection({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={clsx("border-t border-line px-4 py-3", className)}>
      {title !== undefined && (
        <SectionLabel as="h3" inset trailing={actions} className="mb-1.5">
          {title}
        </SectionLabel>
      )}
      {children}
    </section>
  );
}

// --- Tuiles et cartes de choix ------------------------------------------------------------------------------------

/** Tuile chiffrée (R2) : fond `canvas`, libellé en 11,5 px, chiffre clé en 18 px semi-gras. Pas de couleur
 *  décorative : le chiffre n'est coloré que pour un état (`ok`, `warn`, `danger`). */
export function StatTile({
  label,
  value,
  unit,
  hint,
  icon,
  tone = "neutral",
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  hint?: ReactNode;
  /** Icône de 12 px devant le libellé. */
  icon?: IconProp;
  tone?: "neutral" | "ok" | "warn" | "danger";
  className?: string;
}) {
  return (
    <div className={clsx("min-w-0 rounded-lg bg-canvas px-3 py-2", className)}>
      <div className="flex items-center gap-1.5 text-meta leading-snug text-ink-muted">
        {icon !== undefined && <span className="flex shrink-0">{renderIcon(icon, 12)}</span>}
        <span className="min-w-0 break-words">{label}</span>
      </div>
      <div className="mt-0.5 flex items-baseline gap-1">
        <span
          className={clsx(
            "text-stat leading-tight font-semibold tabular-nums",
            tone === "ok" ? "text-ok" : tone === "warn" ? "text-warn" : tone === "danger" ? "text-danger" : "text-ink",
          )}
        >
          {value}
        </span>
        {unit !== undefined && <span className="text-ui text-ink-muted">{unit}</span>}
      </div>
      {hint !== undefined && <div className="mt-0.5 text-meta leading-snug text-ink-muted">{hint}</div>}
    </div>
  );
}

/** Carte de choix (niveau, action du terrain, modèle) : au repos bordure `line-strong`, sélectionnée bordure
 *  `accent` sur fond `accent-soft` et titre en `accent` (sans double anneau). `radio` : pastille ronde et rôle
 *  `radio` (choix exclusif dans un `role="radiogroup"`) ; sinon bouton à bascule (`aria-pressed`). `blocked` +
 *  `onBlocked` : préalable manquant (désignation de l'action attendue) ; `disabled` : requête en cours.
 *
 *  Groupe `radio` : il revient à l'appelant d'envelopper les cartes dans un `role="radiogroup"` (avec
 *  `aria-label`) et de piloter le clavier comme un groupe radio natif — flèches pour changer de choix, une seule
 *  carte tabulable (`tabIndex={selected ? 0 : -1}`). Chaque carte seule reste un bouton tabulable. */
export function ChoiceCard({
  selected = false,
  onClick,
  title,
  detail,
  icon,
  radio = false,
  blocked,
  onBlocked,
  disabled,
  children,
  className,
  ref,
  ...rest
}: BlockableProps & {
  /** Cible d'une désignation (`designate`) : la carte attendue quand une autre est bloquée. */
  ref?: Ref<HTMLButtonElement>;
  selected?: boolean;
  onClick?: () => void;
  title: ReactNode;
  detail?: ReactNode;
  /** Icône de 16 px. */
  icon?: IconProp;
  radio?: boolean;
  disabled?: boolean;
  /** Ordre de tabulation (groupe radio : 0 pour la carte choisie, -1 pour les autres). */
  tabIndex?: number;
  children?: ReactNode;
  className?: string;
  [data: `data-${string}`]: string | undefined;
}) {
  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      role={radio ? "radio" : undefined}
      aria-checked={radio ? selected : undefined}
      aria-pressed={radio ? undefined : selected}
      aria-disabled={blocked || undefined}
      disabled={disabled}
      onClick={(event) => {
        if (blocked) {
          event.preventDefault();
          onBlocked?.(event);
          return;
        }
        onClick?.();
      }}
      className={clsx(
        "flex w-full min-w-0 items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors disabled:opacity-40",
        selected ? "border-accent bg-accent-soft" : "border-line-strong bg-surface",
        blocked ? "opacity-40" : !selected && "hover:bg-hover",
        className,
      )}
    >
      {radio && (
        <span
          aria-hidden
          className={clsx(
            "mt-px flex size-4 shrink-0 items-center justify-center rounded-full border",
            selected ? "border-accent bg-accent" : "border-line-strong bg-surface",
          )}
        >
          {selected && <span className="size-1.5 rounded-full bg-surface" />}
        </span>
      )}
      {icon !== undefined && (
        <span className={clsx("flex shrink-0 pt-px", selected ? "text-accent" : "text-ink-muted")}>{renderIcon(icon, 16)}</span>
      )}
      <span className="min-w-0 flex-1">
        <span className={clsx("block text-ui leading-snug font-semibold break-words", selected ? "text-accent" : "text-ink")}>
          {title}
        </span>
        {detail !== undefined && <span className="mt-0.5 block text-meta leading-snug break-words text-ink-muted">{detail}</span>}
        {children}
      </span>
      {/* Place de la coche toujours réservée : la choisir ne change ni la largeur du texte ni la hauteur de la carte
          (sinon le texte passait à la ligne et poussait tout le conteneur vers le bas, 12/10/2026) ; apparition en
          fondu. */}
      {!radio && (
        <Check
          size={14}
          aria-hidden
          className={clsx("mt-px shrink-0 text-accent transition-opacity", selected ? "opacity-100" : "opacity-0")}
        />
      )}
    </button>
  );
}

// --- Barre d'action et note ---------------------------------------------------------------------------------------

/** Barre d'action collante en bas d'un contenu qui défile (« Enregistrer », « Analyser ») : toujours le dernier
 *  élément du panneau (R7). Dans un `FloatingPanel`, préférer sa prop `footer`, rendue hors de la zone qui
 *  défile. */
export function PanelFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={clsx("sticky bottom-0 z-10 flex items-center gap-2 border-t border-line bg-surface px-4 py-3", className)}>
      {children}
    </div>
  );
}

/** Note d'un panneau (rien à afficher, explication) : texte gris en 12 px dans la gouttière. Jamais de ton
 *  d'erreur : une erreur passe par `Callout tone="danger"` ou `EmptyState variant="error"`. */
export function PanelNote({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={clsx("px-4 py-4 text-ui leading-relaxed text-ink-muted", className)}>{children}</p>;
}

/** Sous-liste repliable d'un groupe (Amis, Ennemis) : ligne d'en-tête avec compteur, contenu déplié en douceur.
 *  `open` et `onToggle` pilotent l'état (l'appelant le garde). */
export function DisclosureRow({
  icon,
  title,
  count,
  open,
  onToggle,
  children,
}: {
  icon?: IconProp;
  title: ReactNode;
  count?: number;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
        className="focus-inset flex min-h-8 w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-hover"
      >
        {icon !== undefined && (
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-ink-muted">{renderIcon(icon, 14)}</span>
        )}
        <span className="min-w-0 flex-1 text-ui leading-snug font-medium break-words text-ink">{title}</span>
        {count !== undefined && <Badge>{count}</Badge>}
        <DisclosureChevron open={open} />
      </button>
      <Disclosure open={open} id={id}>
        {children}
      </Disclosure>
    </div>
  );
}
